import { Avatar, Clickable, Col, Divider, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import { RewardTypeItem, CreateRewardTypeModal, RewardTypeDetailModal } from './components';

const ITEMS_LIMIT = 50;

const RewardTypePanel = ({ visible, closeModal, team }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [createVisible, setCreateVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!visible || !team?._id) return;
    fetchItems(true);
  }, [visible, team?._id]);

  async function fetchItems(reset = false) {
    if (reset) {
      setLoading(true);
      setItems([]);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    const offset = reset ? 0 : items.length;

    try {
      const res = await Api.get(`/admin/rewardType/list?teamId=${team._id}&offset=${offset}&limit=${ITEMS_LIMIT}`);

      if (!res) {
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const newItems = res.data || res || [];
      setHasMore(newItems.length >= ITEMS_LIMIT);

      if (reset) {
        setItems(newItems);
      } else {
        setItems((prev) => [...prev, ...newItems]);
      }
    } catch (error) {
      console.error('Failed to fetch reward types:', error);
    }

    setLoading(false);
    setLoadingMore(false);
  }

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchItems(false);
    }
  }, [loadingMore, hasMore, items.length]);

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm('Are you sure you want to delete this reward type?')) return;

    try {
      const res = await Api.delete(`/admin/rewardType/delete/${item._id}`);

      if (res) {
        fetchItems(true);
      }
    } catch (error) {
      console.error('Failed to delete reward type:', error);
      alert('Failed to delete reward type');
    }
  };

  const getEligibilityBadges = (eligibility) => {
    if (!eligibility) return '';
    const badges = [];
    if (eligibility.bronze?.eligible) badges.push('Bronze');
    if (eligibility.silver?.eligible) badges.push('Silver');
    if (eligibility.gold?.eligible) badges.push('Gold');
    return badges.join(' | ');
  };

  return (
    <Sidepanel pad="24px 0" wid={800} visible={visible} closeModal={closeModal}>
      <Col>
        <Row pad="0 24px 24px" wid="calc(100% - 48px)" start noFlex>
          <Col center>
            <Clickable onClick={closeModal}>
              <FiArrowLeft size={20} style={{ marginRight: 12 }} />
            </Clickable>
          </Col>

          <Avatar user={team} wid="100%" />

          <Row between wid="100%" ht="100%">
            <Col marg="0 0 0 10px" between ht="100%">
              <Col noFlex>
                <Text med>{team?.name}</Text>
                <Text c1 med col={theme.GREY_60}>
                  @{team?.username}
                </Text>
              </Col>

              <Text b2>{items.length} reward types</Text>
            </Col>

            <Clickable onClick={() => setCreateVisible(true)}>
              <FiPlus size={18} />
            </Clickable>
          </Row>
        </Row>

        <Divider />

        {loading ? (
          <Col ht="100%" center pad="24px 0">
            <Spinner2 />
          </Col>
        ) : (
          <Col
            ref={scrollRef}
            onScroll={handleScroll}
            pad="24px"
            start
            wid="100%"
            style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}
          >
            {items.length === 0 ? (
              <Col center pad="40px 0">
                <Text b2 col={theme.GREY_60}>
                  No reward types found
                </Text>
                <Text c1 col={theme.GREY_40} marg="8px 0 0">
                  Click + to create your first reward type
                </Text>
              </Col>
            ) : (
              <Col noFlex wid="100%" gap="12px">
                {items.map((item) => (
                  <RewardTypeItem
                    key={item._id}
                    item={item}
                    onClick={handleItemClick}
                    onDelete={handleDeleteItem}
                  />
                ))}
                {loadingMore && (
                  <Col center pad="16px 0">
                    <Spinner2 />
                  </Col>
                )}
              </Col>
            )}
          </Col>
        )}
      </Col>

      <CreateRewardTypeModal
        visible={createVisible}
        closeModal={() => setCreateVisible(false)}
        team={team}
        existingItems={items}
        onSuccess={() => fetchItems(true)}
      />

      <RewardTypeDetailModal
        visible={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        team={team}
        existingItems={items}
        item={selectedItem}
        onUpdate={() => {
          fetchItems(true);
          setSelectedItem(null);
        }}
        onDelete={() => {
          fetchItems(true);
          setSelectedItem(null);
        }}
      />
    </Sidepanel>
  );
};

export default RewardTypePanel;
